import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../config/api';
import InstallAppButton from '../components/InstallAppButton';
import {
  LayoutDashboard, GraduationCap, BookOpen, Calendar,
  DollarSign, ClipboardCheck, FileText, Megaphone, BarChart3,
  Settings, LogOut, ChevronDown, ChevronRight,
  Building2, UserCheck, BookMarked, Shield, Menu, X,
  MoreHorizontal, Bell, Library, Search, TrendingUp, CalendarDays, BookCopy,
  DoorOpen, ShoppingCart, Users2, CalendarOff, Award, Briefcase, Bed, HandCoins,
  MessageCircle, Send
} from 'lucide-react';

const resultRowStyle: React.CSSProperties = { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 10px', borderRadius: 8, fontFamily: 'inherit' };

function SearchBar() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<{ students: any[]; staff: any[]; courses: any[] } | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    const trimmed = term.trim();
    if (trimmed.length < 2) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get('/search', { params: { q: trimmed } })
        .then(r => setResults(r.data.data))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [term]);

  const goTo = (path: string) => { setOpen(false); setTerm(''); setResults(null); navigate(path); };

  const hasResults = !!results && (results.students.length > 0 || results.staff.length > 0 || results.courses.length > 0);

  return (
    <div ref={ref} style={{ position: 'relative', width: 320 }}>
      <div style={{ position: 'relative' }}>
        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
        <input
          value={term}
          onChange={e => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search students, staff, courses…"
          style={{ width: '100%', padding: '9px 12px 9px 36px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)', fontSize: 13, fontFamily: 'inherit', outline: 'none', color: 'var(--text)' }}
        />
      </div>
      {open && term.trim().length >= 2 && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.12)', maxHeight: 420, overflowY: 'auto', zIndex: 200 }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center' }}><span className="loader loader-dark" /></div>
          ) : !hasResults ? (
            <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>No results for "{term}"</div>
          ) : (
            <div style={{ padding: 8 }}>
              {results!.students.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 10px' }}>Students</div>
                  {results!.students.map(s => (
                    <button key={s.id} onClick={() => goTo(`/students/${s.id}`)} style={resultRowStyle}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{s.first_name} {s.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.student_number} · {s.program_name || 'No program'}</div>
                    </button>
                  ))}
                </div>
              )}
              {results!.staff.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 10px' }}>Staff</div>
                  {results!.staff.map(s => (
                    <button key={s.id} onClick={() => goTo('/staff')} style={resultRowStyle}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{s.first_name} {s.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.designation || 'Staff'} · {s.department_name || 'No department'}</div>
                    </button>
                  ))}
                </div>
              )}
              {results!.courses.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 10px' }}>Courses & Units</div>
                  {results!.courses.map(c => (
                    <button key={c.id} onClick={() => goTo('/academic/units')} style={resultRowStyle}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.code} · {c.program_name || 'No program'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationsBell({ mobile }: { mobile?: boolean }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const r = await api.get('/communications/notifications');
      setNotifications(r.data.data.notifications || []);
      setUnread(r.data.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const markRead = async (id: string) => { await api.patch(`/communications/notifications/${id}/read`); fetchNotifications(); };
  const markAllRead = async () => { await api.patch('/communications/notifications/all/read'); fetchNotifications(); };

  const typeColor = (type: string): string => ({ success: 'var(--success)', alert: 'var(--danger)', warning: 'var(--warning)' } as Record<string,string>)[type] || 'var(--primary)';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ position: 'relative', width: mobile ? 34 : 36, height: mobile ? 34 : 36, borderRadius: '50%', background: 'var(--bg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
      >
        <Bell size={16} color="var(--text-secondary)" />
        {unread > 0 && (
          <span style={{ position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, borderRadius: 8, background: 'var(--danger)', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 340, maxWidth: '90vw', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.14)', zIndex: 200, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Notifications</span>
            {unread > 0 && <button onClick={markAllRead} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>Mark all read</button>}
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No notifications yet</div>
            ) : notifications.slice(0, 8).map(n => (
              <button
                key={n.id}
                onClick={() => { if (!n.is_read) markRead(n.id); setOpen(false); navigate(n.type === 'visitor' ? '/gate' : '/communications'); }}
                style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '12px 16px', background: n.is_read ? 'none' : 'var(--primary-light)', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: typeColor(n.type), marginTop: 6, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: n.is_read ? 500 : 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}>{n.message}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </button>
            ))}
          </div>
          <button onClick={() => { setOpen(false); navigate('/communications'); }} style={{ width: '100%', padding: '12px', background: 'var(--bg)', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: 13, fontFamily: 'inherit' }}>
            View all
          </button>
        </div>
      )}
    </div>
  );
}

const superAdminNav = [
  { to: '/superadmin',               icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/superadmin/institutions',  icon: Building2,       label: 'Institutions' },
  { to: '/reports/audit',            icon: Shield,          label: 'Audit Logs' },
];

const adminNav = [
  { to: '/dashboard',    icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/students',     icon: GraduationCap,   label: 'Students' },
  { to: '/staff',        icon: UserCheck,       label: 'Staff & HR' },
  { to: '/leave',        icon: CalendarOff,     label: 'Leave' },
  { label: 'Academic', icon: BookOpen, children: [
    { to: '/academic/departments', label: 'Departments' },
    { to: '/academic/programs',    label: 'Programs' },
    { to: '/academic/units',       label: 'Units' },
    { to: '/academic/timetable',   label: 'Timetable' },
  ]},
  { to: '/admissions',   icon: ClipboardCheck,  label: 'Admissions' },
  { label: 'Fees & Finance', icon: DollarSign, children: [
    { to: '/fees/structures', label: 'Fee Structures' },
    { to: '/fees/payments',   label: 'Payments' },
    { to: '/fees/defaulters', label: 'Defaulters' },
    { to: '/scholarships',    label: 'Scholarships & Aid' },
  ]},
  { to: '/attendance',   icon: Calendar,        label: 'Attendance' },
  { to: '/exams',        icon: BookMarked,      label: 'Exams' },
  { to: '/results',      icon: FileText,        label: 'Results' },
  { to: '/competencies', icon: Award,           label: 'Competencies & Trades' },
  { to: '/attachments',  icon: Briefcase,       label: 'Attachments' },
  { to: '/hostel',       icon: Bed,             label: 'Hostel' },
  { to: '/materials',    icon: Library,         label: 'Learning Materials' },
  { to: '/library',      icon: BookCopy,        label: 'Library' },
  { to: '/gate',         icon: DoorOpen,        label: 'Gate Management' },
  { to: '/procurement',  icon: ShoppingCart,    label: 'Procurement' },
  { to: '/calendar',     icon: CalendarDays,    label: 'Calendar' },
  { to: '/communications', icon: Megaphone,     label: 'Communications' },
  { label: 'Reports', icon: BarChart3, children: [
    { to: '/reports/financial', label: 'Financial' },
    { to: '/reports/analytics', label: 'Analytics' },
    { to: '/reports/audit',     label: 'Audit Logs' },
  ]},
  { label: 'Settings', icon: Settings, children: [
    { to: '/settings',           label: 'Institution' },
    { to: '/settings/users',     label: 'Users' },
    { to: '/settings/guardians', label: 'Guardians' },
  ]},
];

const registrarNav = [
  { to: '/dashboard',    icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/students',     icon: GraduationCap,   label: 'Students' },
  { to: '/leave',        icon: CalendarOff,     label: 'Leave' },
  { label: 'Academic', icon: BookOpen, children: [
    { to: '/academic/departments', label: 'Departments' },
    { to: '/academic/programs',    label: 'Programs' },
    { to: '/academic/units',       label: 'Units' },
    { to: '/academic/timetable',   label: 'Timetable' },
  ]},
  { to: '/admissions',   icon: ClipboardCheck,  label: 'Admissions' },
  { to: '/attendance',   icon: Calendar,        label: 'Attendance' },
  { to: '/exams',        icon: BookMarked,      label: 'Exams' },
  { to: '/results',      icon: FileText,        label: 'Results' },
  { to: '/competencies', icon: Award,           label: 'Competencies & Trades' },
  { to: '/attachments',  icon: Briefcase,       label: 'Attachments' },
  { to: '/hostel',       icon: Bed,             label: 'Hostel' },
  { to: '/materials',    icon: Library,         label: 'Learning Materials' },
  { to: '/library',      icon: BookCopy,        label: 'Library' },
  { to: '/gate',         icon: DoorOpen,        label: 'Gate Management' },
  { to: '/settings/guardians', icon: Users2,    label: 'Guardians' },
  { to: '/calendar',     icon: CalendarDays,    label: 'Calendar' },
  { to: '/communications', icon: Megaphone,     label: 'Communications' },
  { to: '/reports/analytics', icon: BarChart3,  label: 'Analytics' },
  { to: '/reports/audit', icon: Shield,         label: 'Audit Logs' },
];

const financeNav = [
  { to: '/dashboard',    icon: LayoutDashboard, label: 'Dashboard' },
  { label: 'Fees & Finance', icon: DollarSign, children: [
    { to: '/fees/structures', label: 'Fee Structures' },
    { to: '/fees/payments',   label: 'Payments' },
    { to: '/fees/defaulters', label: 'Defaulters' },
    { to: '/scholarships',    label: 'Scholarships & Aid' },
  ]},
  { to: '/leave',             icon: CalendarOff, label: 'Leave' },
  { to: '/procurement',       icon: ShoppingCart, label: 'Procurement' },
  { to: '/reports/financial', icon: BarChart3,  label: 'Financial Reports' },
  { to: '/reports/analytics', icon: TrendingUp, label: 'Analytics' },
  { to: '/calendar',          icon: CalendarDays, label: 'Calendar' },
  { to: '/communications',    icon: Megaphone,  label: 'Communications' },
];

const lecturerNav = [
  { to: '/dashboard',      icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/attendance',     icon: Calendar,        label: 'Attendance' },
  { to: '/exams',          icon: BookMarked,      label: 'Exams' },
  { to: '/results',        icon: FileText,        label: 'Results' },
  { to: '/competencies',   icon: Award,           label: 'Competencies & Trades' },
  { to: '/attachments',    icon: Briefcase,       label: 'Attachments' },
  { to: '/materials',      icon: Library,         label: 'Learning Materials' },
  { to: '/library',        icon: BookCopy,        label: 'Library' },
  { to: '/leave',          icon: CalendarOff,     label: 'Leave' },
  { to: '/gate',           icon: DoorOpen,        label: 'Gate Passes' },
  { to: '/procurement',    icon: ShoppingCart,    label: 'Procurement' },
  { to: '/calendar',       icon: CalendarDays,    label: 'Calendar' },
  { to: '/communications', icon: Megaphone,       label: 'Communications' },
];

const securityNav = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/gate',       icon: DoorOpen,        label: 'Gate Management' },
  { to: '/leave',      icon: CalendarOff,     label: 'Leave' },
];

const parentNav = [
  { to: '/dashboard',      icon: LayoutDashboard, label: 'My Children' },
  { to: '/calendar',       icon: CalendarDays,    label: 'Calendar' },
  { to: '/communications', icon: Megaphone,       label: 'Announcements' },
];

const studentNav = [
  { to: '/dashboard',      icon: LayoutDashboard, label: 'My Dashboard' },
  { to: '/exams',          icon: BookMarked,      label: 'My Exams' },
  { to: '/results',        icon: FileText,        label: 'My Results' },
  { to: '/competencies',   icon: Award,           label: 'My Competencies' },
  { to: '/attachments',    icon: Briefcase,       label: 'My Attachment' },
  { to: '/materials',      icon: Library,         label: 'Learning Materials' },
  { to: '/library',        icon: BookCopy,        label: 'Library' },
  { to: '/gate',           icon: DoorOpen,        label: 'Gate Passes' },
  { to: '/hostel',         icon: Bed,             label: 'Hostel' },
  { to: '/calendar',       icon: CalendarDays,    label: 'Calendar' },
  { to: '/attendance',     icon: Calendar,        label: 'My Attendance' },
  { to: '/fees/payments',  icon: DollarSign,      label: 'My Fees' },
  { to: '/scholarships',   icon: HandCoins,       label: 'Scholarships & Aid' },
  { to: '/communications', icon: Megaphone,       label: 'Announcements' },
];

function getNav(role: string) {
  switch (role) {
    case 'superadmin': return superAdminNav;
    case 'admin':      return adminNav;
    case 'registrar':  return registrarNav;
    case 'finance':    return financeNav;
    case 'lecturer':   return lecturerNav;
    case 'student':    return studentNav;
    case 'security':   return securityNav;
    case 'parent':     return parentNav;
    default:           return adminNav;
  }
}

function getMobileTabs(role: string) {
  switch (role) {
    case 'superadmin': return [
      { to: '/superadmin',              icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/superadmin/institutions', icon: Building2,       label: 'Institutions' },
      { to: '/reports/audit',           icon: Shield,          label: 'Audit' },
    ];
    case 'finance': return [
      { to: '/dashboard',      icon: LayoutDashboard, label: 'Home' },
      { to: '/fees/payments',  icon: DollarSign,      label: 'Payments' },
      { to: '/fees/defaulters',icon: BarChart3,       label: 'Defaulters' },
    ];
    case 'lecturer': return [
      { to: '/dashboard',      icon: LayoutDashboard, label: 'Home' },
      { to: '/attendance',     icon: Calendar,        label: 'Attendance' },
      { to: '/materials',      icon: Library,         label: 'Materials' },
      { to: '/exams',          icon: BookMarked,      label: 'Exams' },
    ];
    case 'student': return [
      { to: '/dashboard',      icon: LayoutDashboard, label: 'Home' },
      { to: '/exams',          icon: BookMarked,      label: 'Exams' },
      { to: '/materials',      icon: Library,         label: 'Materials' },
      { to: '/results',        icon: FileText,        label: 'Results' },
    ];
    case 'security': return [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Home' },
      { to: '/gate',       icon: DoorOpen,        label: 'Gate' },
    ];
    case 'parent': return [
      { to: '/dashboard',      icon: LayoutDashboard, label: 'Home' },
      { to: '/calendar',       icon: CalendarDays,    label: 'Calendar' },
      { to: '/communications', icon: Megaphone,       label: 'News' },
    ];
    default: return [
      { to: '/dashboard',      icon: LayoutDashboard, label: 'Home' },
      { to: '/students',       icon: GraduationCap,   label: 'Students' },
      { to: '/fees/payments',  icon: DollarSign,      label: 'Fees' },
      { to: '/attendance',     icon: Calendar,        label: 'Attendance' },
    ];
  }
}

function NavItem({ item, collapsed }: { item: any; collapsed: boolean }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isChildActive = item.children?.some((c: any) => location.pathname.startsWith(c.to));

  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setOpen(!open)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '10px' : '10px 14px', borderRadius: 8, background: isChildActive ? 'var(--primary-light)' : 'none', border: 'none', cursor: 'pointer', color: isChildActive ? 'var(--primary)' : 'var(--text-secondary)', fontSize: 14, fontFamily: 'inherit', fontWeight: isChildActive ? 600 : 500, justifyContent: collapsed ? 'center' : 'space-between', transition: 'all 0.15s' }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <item.icon size={18} />
            {!collapsed && item.label}
          </span>
          {!collapsed && (open || isChildActive ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
        </button>
        {(open || isChildActive) && !collapsed && (
          <div style={{ paddingLeft: 28, marginTop: 2 }}>
            {item.children.map((child: any) => (
              <NavLink
                key={child.to}
                to={child.to}
                style={({ isActive }) => ({ display: 'block', padding: '7px 14px', borderRadius: 6, fontSize: 13, color: isActive ? 'var(--primary)' : 'var(--text-muted)', background: isActive ? 'var(--primary-light)' : 'transparent', fontWeight: isActive ? 600 : 400, textDecoration: 'none', marginBottom: 1, transition: 'all 0.15s' })}
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/dashboard' || item.to === '/superadmin'}
      title={collapsed ? item.label : undefined}
      style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '10px' : '10px 14px', borderRadius: 8, textDecoration: 'none', color: isActive ? 'var(--primary)' : 'var(--text-secondary)', background: isActive ? 'var(--primary-light)' : 'transparent', fontWeight: isActive ? 600 : 500, fontSize: 14, transition: 'all 0.15s', justifyContent: collapsed ? 'center' : 'flex-start' })}
    >
      <item.icon size={18} />
      {!collapsed && item.label}
    </NavLink>
  );
}

function HelpAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; text: string }[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages(m => [...m, { role: 'user', text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await api.post('/assistant/ask', { message: text });
      setMessages(m => [...m, { role: 'assistant', text: res.data.data.reply }]);
    } catch {
      setMessages(m => [...m, { role: 'assistant', text: "Sorry, something went wrong. Try again in a moment." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ position: 'fixed', bottom: 24, right: 24, width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,var(--primary),#0e9f6e)', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}
        title="Help"
      >
        <MessageCircle size={22} color="#fff" />
      </button>
      {open && (
        <div style={{ position: 'fixed', bottom: 88, right: 24, width: 340, maxWidth: '90vw', height: 440, maxHeight: '70vh', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 16px 40px rgba(0,0,0,0.18)', zIndex: 300, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>CTIMS Help</div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {messages.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Ask about fees, attendance, leave, hostel, attachments, scholarships, or anything else in CTIMS.</div>}
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '8px 12px', borderRadius: 12, fontSize: 13, background: m.role === 'user' ? 'var(--primary)' : 'var(--bg)', color: m.role === 'user' ? '#fff' : 'var(--text)' }}>
                {m.text}
              </div>
            ))}
            {loading && <div style={{ alignSelf: 'flex-start', fontSize: 12, color: 'var(--text-muted)' }}>Thinking…</div>}
          </div>
          <div style={{ display: 'flex', gap: 6, padding: 10, borderTop: '1px solid var(--border)' }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send(); }}
              placeholder="Type a question..."
              style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', fontSize: 13, fontFamily: 'inherit', outline: 'none', color: 'var(--text)' }}
            />
            <button onClick={send} disabled={loading} style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--primary)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Send size={15} color="#fff" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const nav = getNav(user?.role || 'admin');
  const mobileTabs = getMobileTabs(user?.role || 'admin');

  const handleLogout = () => { logout(); navigate('/login'); };


  const SidebarContent = ({ onNavClick }: { onNavClick?: () => void }) => (
    <>
      <div style={{ padding: '16px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, minHeight: 64 }}>
        <div style={{ width: 32, height: 32, background: 'linear-gradient(135deg,var(--primary),#0e9f6e)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <GraduationCap size={18} color="#fff" />
        </div>
        {!collapsed && (
          <div style={{ overflow: 'hidden', flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', whiteSpace: 'nowrap' }}>CTIMS</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Helvino Tech</div>
          </div>
        )}
        {!onNavClick && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4, borderRadius: 6, flexShrink: 0 }}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </button>
        )}
        {onNavClick && (
          <button onClick={onNavClick} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex', borderRadius: 6 }}>
            <X size={20} />
          </button>
        )}
      </div>

      {user?.role !== 'superadmin' && (
        <div style={{ padding: '10px 14px', background: 'var(--primary-light)', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{user?.institution_type || 'Institution'}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.institution_name}</div>
        </div>
      )}
      {user?.role === 'superadmin' && (
        <div style={{ padding: '10px 14px', background: '#fef3c7', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: '#92400e', fontWeight: 600 }}>SUPER ADMINISTRATOR</div>
          <div style={{ fontSize: 12, color: '#78350f' }}>Helvino Technologies</div>
        </div>
      )}

      <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {nav.map((item, i) => (
            <div key={i} onClick={onNavClick}>
              <NavItem item={item} collapsed={collapsed && !onNavClick} />
            </div>
          ))}
        </div>
      </nav>

      <div style={{ padding: '12px 8px', borderTop: '1px solid var(--border)' }}>
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, marginBottom: 4 }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,var(--primary),#0e9f6e)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div style={{ overflow: 'hidden', flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.first_name} {user?.last_name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{user?.role}</div>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: collapsed ? 10 : '8px 10px', borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 14, fontFamily: 'inherit', fontWeight: 500, justifyContent: collapsed ? 'center' : 'flex-start' }}
        >
          <LogOut size={16} />
          {!collapsed && 'Logout'}
        </button>
      </div>
    </>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      {/* ── Desktop sidebar ── */}
      <aside className={`dashboard-sidebar${collapsed ? ' collapsed' : ''}`}>
        <SidebarContent />
      </aside>

      {/* ── Mobile drawer overlay ── */}
      <div className={`mobile-drawer-overlay${drawerOpen ? ' open' : ''}`} onClick={() => setDrawerOpen(false)} />

      {/* ── Mobile drawer ── */}
      <div className={`mobile-drawer${drawerOpen ? ' open' : ''}`}>
        <SidebarContent onNavClick={() => setDrawerOpen(false)} />
      </div>

      {/* ── Main content area ── */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Desktop top header */}
        <header className="dashboard-header-desktop" style={{ height: 60, background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', flexShrink: 0 }}>
          {user?.role !== 'superadmin' ? <SearchBar /> : <div />}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <InstallAppButton variant="icon" />
            <NotificationsBell />
            <button
              onClick={() => navigate('/profile')}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,var(--primary),#0e9f6e)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{user?.first_name}</span>
            </button>
          </div>
        </header>

        {/* Mobile top bar */}
        <div className="mobile-topbar">
          <button
            onClick={() => setDrawerOpen(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 6, borderRadius: 8, color: 'var(--text)' }}
          >
            <Menu size={22} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 26, height: 26, background: 'linear-gradient(135deg,var(--primary),#0e9f6e)', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GraduationCap size={14} color="#fff" />
            </div>
            <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>CTIMS</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InstallAppButton variant="icon" />
            <NotificationsBell mobile />
            <button
              onClick={() => navigate('/profile')}
              style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,var(--primary),#0e9f6e)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}
            >
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </button>
          </div>
        </div>

        {/* Page content */}
        <div className="dashboard-main-content fade-in" style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          <Outlet />
        </div>
      </main>

      {/* ── Mobile bottom tab bar (Facebook-style) ── */}
      <div className="mobile-bottom-nav">
        <div className="mobile-bottom-nav-inner">
          {mobileTabs.map(tab => {
            const isActive = location.pathname === tab.to || (tab.to !== '/dashboard' && tab.to !== '/superadmin' && location.pathname.startsWith(tab.to));
            return (
              <button
                key={tab.to}
                className={`mob-tab${isActive ? ' active' : ''}`}
                onClick={() => navigate(tab.to)}
              >
                <tab.icon size={22} strokeWidth={isActive ? 2.5 : 1.8} />
                <span>{tab.label}</span>
              </button>
            );
          })}
          <button
            className={`mob-tab${drawerOpen ? ' active' : ''}`}
            onClick={() => setDrawerOpen(true)}
          >
            <MoreHorizontal size={22} strokeWidth={1.8} />
            <span>More</span>
          </button>
        </div>
      </div>

      <HelpAssistant />
    </div>
  );
}
